import React from 'react';
import { useTranslation } from 'react-i18next';
import { DealRoom } from 'models/DealRoom';
import {
  checkIsPublishedOrPrivateDrafted,
  removeLINK,
  transformPriceNumberToCurrencyString,
} from 'utils';
import { DEAL_STATUS_LABEL } from 'data/dealStatus';
import { AnalyticsDealsTable } from './data';
import useViewPortfolioAs from 'hooks/useViewPortfolioAs';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import XcircleIcon from 'icons/Xcircle.icon';
import classNames from 'classnames';

type CellProps = {
  deal: DealRoom;
};

const NameCell: React.FC<CellProps> = ({ deal }) => {
  const { portfolioName } = deal;

  return <div className="pdt-name-cell">{portfolioName}</div>;
};

const StageCell: React.FC<CellProps> = ({ deal }) => {
  const { stage } = deal;

  const { t } = useTranslation();

  return <div className="pdt-stage-cell">{t(DEAL_STATUS_LABEL[stage])}</div>;
};

const HeadCell: React.FC<CellProps> = ({ deal }) => {
  const { colleaguesCount } = deal;

  return <div className="pdt-head-count-cell">{colleaguesCount || 0}</div>;
};

const OwnerCell: React.FC<CellProps> = ({ deal }) => {
  const { ownerName, userId } = deal;

  const navigateToUsersPortfolio = useViewPortfolioAs();

  return (
    <div
      className="pdt-owner-cell"
      onClick={(e) => {
        e.stopPropagation();
        navigateToUsersPortfolio(userId, ownerName);
      }}
    >
      {ownerName}
    </div>
  );
};

const LocationCell: React.FC<CellProps> = ({ deal }) => {
  const { ownerBusinessUnit } = deal;

  if (!ownerBusinessUnit) return null;

  return (
    <div className="pdt-location-cell">{removeLINK(ownerBusinessUnit)}</div>
  );
};

const SuccessFeeCell: React.FC<CellProps> = ({ deal }) => {
  const { successFeeShare } = deal;

  if (!successFeeShare) return null;

  return (
    <div className="pdt-success-fee-cell">
      {transformPriceNumberToCurrencyString(successFeeShare)}
    </div>
  );
};

const ListingCell: React.FC<CellProps> = ({ deal }) => {
  const { listingStage, wantToBePrivate } = deal;

  const hasListing = checkIsPublishedOrPrivateDrafted(
    listingStage,
    wantToBePrivate,
  );

  return (
    <div
      className={classNames('pdt-listing-cell', { 'has-listing': hasListing })}
    >
      {hasListing ? <CheckcircleIcon /> : <XcircleIcon />}
    </div>
  );
};

const LeadsCell: React.FC<CellProps> = ({ deal }) => {
  const { numberOfLeads } = deal;

  return <div className="pdt-leads-cell">{numberOfLeads}</div>;
};

const FilesCell: React.FC<CellProps> = ({ deal }) => {
  const { numberOfFiles } = deal;

  return <div className="pdt-files-cell">{numberOfFiles}</div>;
};

export const BODY_CONTENT_GENERATE: Record<
  keyof AnalyticsDealsTable,
  (cellProps: CellProps) => React.ReactNode
> = {
  name: (cellProps: CellProps) => <NameCell {...cellProps} />,
  stage: (cellProps: CellProps) => <StageCell {...cellProps} />,
  listing: (cellProps: CellProps) => <ListingCell {...cellProps} />,
  numberOfLeads: (cellProps: CellProps) => <LeadsCell {...cellProps} />,
  numberOfFiles: (cellProps: CellProps) => <FilesCell {...cellProps} />,
  colleaguesCount: (cellProps: CellProps) => <HeadCell {...cellProps} />,
  ownerBusinessUnit: (cellProps: CellProps) => <LocationCell {...cellProps} />,
  ownerName: (cellProps: CellProps) => <OwnerCell {...cellProps} />,
  successFeeShare: (cellProps: CellProps) => <SuccessFeeCell {...cellProps} />,
};
